import { useFormContext } from 'react-hook-form';
import { Calendar, X, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/auth/auth-store';
import {
  usePreviousAppointments,
  type PreviousAppointment,
} from '@/services/scheduling/use-previous-appointments';
import type { AppointmentFormValues } from '@/types/appointment-form';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(raw: string | undefined): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${MONTH_SHORT[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

/**
 * PreviousAppointmentSection — picks the reference appointment for a
 * follow-up. Mirrors Flutter `previous_appointments_widget.dart`.
 *
 * Visible when `appointmentType.code === 'FOLLOWUP'` AND a patient is
 * loaded. On select, copies appointmentId → referenceAppointmentId and
 * caches RFV / location / date for the selected card. Also writes the
 * picked reasonCode into the appointmentProfile.reasonCode (Flutter
 * create_appointment_widget.dart:605–617).
 */
export function PreviousAppointmentSection() {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const apptType = watch('appointmentType');
  const patientId = watch('patientId');
  const providerId = watch('providerId');
  const referenceAppointmentId = watch('referenceAppointmentId');
  const previousRFV = watch('previousAppointmentRFV');
  const previousLocation = watch('previousAppointmentLocation');
  const previousDate = watch('previousAppointmentDate');
  const deletable = watch('previousAppointmentDeletable');

  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  // RACE (Samer review on PR #408 §4): `user.practiceId` can be transiently
  // empty here. The api-client injects the PracticeId HEADER from the active
  // practice (which resolves first), but THIS field on the user object lags
  // on some session-boot / edit-page-deep-link paths — so the query could
  // fire with practiceId="" and 400 ("converting '' to System.Guid") before
  // the field populates. The hook's `enabled` guard (non-empty practiceId +
  // FOLLOWUP) defends against it; this is NOT a root-cause fix for why the
  // field is briefly empty. If the data-loading order changes, re-check here.
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';

  const { data: list = [], isLoading } = usePreviousAppointments({
    organizationId: orgId,
    practiceId,
    providerId,
    patientId,
    // Flutter loads previous appointments only in the FOLLOWUP flow — the
    // section below already returns null otherwise, so gate the query too
    // (avoids a wasted call + an empty-practiceId 400 in edit mode).
    enabled: apptType?.code === 'FOLLOWUP',
  });

  if (apptType?.code !== 'FOLLOWUP' || !patientId) return null;

  const selectAppointment = (prev: PreviousAppointment) => {
    if (!prev.appointmentId) return;
    setValue('referenceAppointmentId', prev.appointmentId, { shouldValidate: true });
    setValue('previousAppointmentRFV', prev.reasonCode?.display ?? null);
    setValue(
      'previousAppointmentLocation',
      prev.appointmentProfile?.location ?? null,
    );
    setValue('previousAppointmentDate', prev.appointmentDate ?? null);
    if (prev.reasonCode?.code) {
      setValue(
        'reasonCode',
        {
          code: prev.reasonCode.code,
          display: prev.reasonCode.display ?? '',
          definition: prev.reasonCode.definition,
        },
        { shouldValidate: true },
      );
    }
  };

  const clear = () => {
    setValue('referenceAppointmentId', null, { shouldValidate: true });
    setValue('previousAppointmentRFV', null);
    setValue('previousAppointmentLocation', null);
    setValue('previousAppointmentDate', null);
  };

  const isSelected = !!referenceAppointmentId;

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <p className="text-xs font-medium text-text-muted mb-2">
        Previous appointment{isSelected ? '' : 's'}
      </p>

      {isSelected ? (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
          <Calendar className="h-4 w-4 text-brand-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">
              RFV: {previousRFV ?? '—'}
            </p>
            <p className="text-xs text-text-muted truncate">
              {fmtDate(previousDate ?? undefined)}
              {previousLocation?.name ? ` · ${previousLocation.name}` : ''}
            </p>
          </div>
          {deletable ? (
            <button
              type="button"
              onClick={clear}
              className="p-1 hover:bg-grey-100 rounded"
              aria-label="Clear previous appointment"
            >
              <X className="h-4 w-4 text-status-error" />
            </button>
          ) : null}
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-grey-200 animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-100">
          <AlertCircle className="h-4 w-4 text-status-error flex-shrink-0 mt-1" />
          <p className="text-xs text-red-700 leading-snug">
            No previous appointments found. Please proceed with a first-time appointment.
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[184px] overflow-y-auto scrollbar-hide pr-1">
          {list.map((prev, i) => (
            <button
              key={prev.appointmentId ?? `prev-${i}`}
              type="button"
              onClick={() => selectAppointment(prev)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card hover:border-brand-primary hover:bg-blue-100 text-left transition-colors"
            >
              <Calendar className="h-4 w-4 text-brand-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">
                  RFV: {prev.reasonCode?.display ?? '—'}
                </p>
                <p className="text-xs text-text-muted truncate">
                  {fmtDate(prev.appointmentDate)}
                  {prev.appointmentProfile?.location?.name
                    ? ` · ${prev.appointmentProfile.location.name}`
                    : ''}
                </p>
              </div>
              <span className="text-xs font-medium text-brand-primary flex-shrink-0">
                Select
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
